/**
 * Issue #346 — 数据总览「KPI 考核」筛选状态 → 后端查询映射 + 展示纯逻辑（可完整单测）。
 * 分页 / 搜索 / 排序全走后端 GET /admin/kpi；本模块不做客户端切片。
 */
import type {
  AdminKpiQuery,
  KpiGranularity,
  KpiSortBy,
  SortOrder,
} from './kpi-api';

export const KPI_PAGE_SIZE_OPTIONS = [20, 50, 100] as const;
export const KPI_DEFAULT_PAGE_SIZE = 20;

export const KPI_GRANULARITY_OPTIONS: readonly {
  value: KpiGranularity;
  label: string;
}[] = [
  { value: 'day', label: '按日' },
  { value: 'week', label: '按周' },
  { value: 'month', label: '按月' },
];

export const KPI_SORT_OPTIONS: readonly { value: KpiSortBy; label: string }[] = [
  { value: 'totalPoints', label: '总积分' },
  { value: 'onlineDurationSec', label: '在线时长' },
  { value: 'onlineTimePoints', label: '在线积分' },
  { value: 'learningPoints', label: '学习积分' },
  { value: 'qcPoints', label: '质检积分' },
  { value: 'name', label: '姓名' },
];

export interface KpiFilterState {
  granularity: KpiGranularity;
  /** 锚定日期 YYYY-MM-DD；空串=今天（由后端解释）。 */
  date: string;
  keyword: string;
  deptId: string;
  sortBy: KpiSortBy;
  sortOrder: SortOrder;
  page: number;
  pageSize: number;
}

export const DEFAULT_KPI_FILTER: KpiFilterState = {
  granularity: 'month',
  date: '',
  keyword: '',
  deptId: '',
  sortBy: 'totalPoints',
  sortOrder: 'desc',
  page: 1,
  pageSize: KPI_DEFAULT_PAGE_SIZE,
};

export function normalizeKpiPageSize(value: number): number {
  return (KPI_PAGE_SIZE_OPTIONS as readonly number[]).includes(value)
    ? value
    : KPI_DEFAULT_PAGE_SIZE;
}

/** 把筛选状态映射为后端查询参数（去空白空串省略；pageSize 归一化）。 */
export function buildKpiQuery(state: KpiFilterState): AdminKpiQuery {
  const keyword = state.keyword.trim();
  const date = state.date.trim();
  const deptId = state.deptId.trim();
  return {
    granularity: state.granularity,
    date: date.length > 0 ? date : undefined,
    keyword: keyword.length > 0 ? keyword : undefined,
    deptId: deptId.length > 0 ? deptId : undefined,
    page: Math.max(1, Math.trunc(state.page) || 1),
    pageSize: normalizeKpiPageSize(state.pageSize),
    sortBy: state.sortBy,
    sortOrder: state.sortOrder,
  };
}

/** 秒数 → 「约 N 小时 M 分」展示（文案含「约」，呼应审计推导的近似口径）。 */
export function formatOnlineDuration(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `约 ${h} 小时${m > 0 ? ` ${m} 分` : ''}`;
  if (m > 0) return `约 ${m} 分`;
  return '约 0 分';
}
